import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AmdocsAuthService } from 'amdocs-core-package';

/**
 * Ensures open_id session is valid before app GraphQL/HTTP APIs run.
 * If open_id is expired but CAS session exists → refresh first.
 * If both are invalid → start SSO login (no API call with dead token).
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshInFlight$: Observable<unknown> | null = null;

  constructor(private authService: AmdocsAuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldSkipAuth(req)) {
      return next.handle(req);
    }

    if (this.authService.isLoggedIn()) {
      return next.handle(req);
    }

    if (this.authService.hasRefreshTokenCookies()) {
      return this.refreshTokenOnce().pipe(
        switchMap(() => next.handle(req)),
        catchError((error) => {
          // callRefreshUserToken already triggers SSO on failure
          return throwError(() => error);
        })
      );
    }

    this.authService.initGetLoginUrlFlow().subscribe({
      error: (err) => console.error('SSO login redirect failed:', err)
    });
    return throwError(() => new Error('Session expired. Redirecting to login.'));
  }

  private shouldSkipAuth(req: HttpRequest<any>): boolean {
    const url = (req.url || '').toLowerCase();
    // Login gateway / OIDC must not be blocked by this interceptor
    return url.includes('/oidc/') || /\/cas([-\/?]|$)/.test(url);
  }

  private refreshTokenOnce(): Observable<unknown> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authService.callRefreshUserToken().pipe(
        tap(() => {
          // Keep package refresh interval in sync after a successful renew
          this.authService.setRefreshTokenInterval();
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );
    }
    return this.refreshInFlight$;
  }
}
